/**
 * ============================================================================
 * useDeviationDetailQuery — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Purpose: TanStack Query hook for the deviation detail screen. Returns the
 *          full deviation envelope: master + investigation notes (sorted
 *          ascending) + full history chain + dual-sign state.
 * Endpoint: GET /api/v1/deviations/{id}.
 *
 * URS rows: DEV-002 (detail view), DEV-005 (dual-sign UI), DEV-006 (5-Why),
 *           DEV-007 (Fishbone), DEV-008 (RCA traceability).
 * ============================================================================ */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  DeviationClassification,
  DeviationStatus,
  SourceArea,
} from '../DeviationTriage/useDeviationsQuery';

export type NoteKind =
  | 'OBSERVATION'
  | '5_WHY'
  | 'FISHBONE'
  | 'EVIDENCE_LINK'
  | 'INTERVIEW'
  | 'RCA_HYPOTHESIS'
  | 'RCA_CONFIRMED';

export interface DualSignState {
  required: boolean;
  primarySignatureId: string | null;
  secondarySignatureId: string | null;
  satisfied: boolean;
}

export interface NoteEntry {
  noteId: string;
  kind: NoteKind;
  body: string;
  authoredByUserId: string;
  authoredAtUtc: string;
  evidenceDocId: string | null;
}

export interface HistoryEntry {
  eventId: string;
  eventCode: string;
  eventAtUtc: string;
  actorUserId: string | null;
  payload: Record<string, unknown>;
}

export interface DeviationDetailPayload {
  deviationId: string;
  tenantId: string;
  deviationNumber: string;
  title: string;
  description: string;
  classification: DeviationClassification;
  sourceArea: SourceArea;
  affectedEquipmentIds: string[];
  affectedDocIds: string[];
  affectedBatchRefs: string[];
  status: DeviationStatus;
  occurredAtUtc: string;
  discoveredAtUtc: string;
  reportedAtUtc: string;
  reporterUserId: string;
  investigatorUserId: string | null;
  slaDueUtc: string | null;
  slaBreached: boolean;
  rcaSummary: string | null;
  rcaCompletedAtUtc: string | null;
  requiresCapa: boolean;
  capaId: string | null;
  closedAtUtc: string | null;
  dualSign: DualSignState;
  notes: NoteEntry[];
  history: HistoryEntry[];
  // C-9 — GMP impact assessment + containment + investigation extensions.
  immediateActions: string | null;
  impact: ImpactAssessment;
  extensions: ExtensionRow[];
}

export type ImpactSeverity = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';
export interface ImpactAssessment {
  product: ImpactSeverity;
  patient: ImpactSeverity;
  dataIntegrity: ImpactSeverity;
  regulatory: ImpactSeverity;
  notes: string | null;
}
export interface ExtensionRow {
  extensionId: string;
  requestedAtUtc: string;
  previousDueUtc: string | null;
  requestedDueUtc: string;
  justification: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  decidedAtUtc: string | null;
  decisionNote: string | null;
  requestedByName: string | null;
  decidedByName: string | null;
}

export function useDeviationDetailQuery(
  deviationId: string | undefined,
): UseQueryResult<DeviationDetailPayload> {
  return useQuery<DeviationDetailPayload>({
    queryKey: ['deviation', 'detail', deviationId],
    queryFn: ({ signal }) =>
      apiFetch<DeviationDetailPayload>(`/api/v1/deviations/${deviationId}`, { signal }),
    enabled: !!deviationId,
  });
}

// C-9 — save the impact assessment + immediate actions + affected batches.
export function useDeviationImpact(
  deviationId: string | undefined,
): UseMutationResult<unknown, Error, { immediateActions?: string; impact?: Partial<ImpactAssessment>; affectedBatchRefs?: string[] }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { immediateActions?: string; impact?: Partial<ImpactAssessment>; affectedBatchRefs?: string[] }) =>
      apiFetch(`/api/v1/deviations/${encodeURIComponent(deviationId!)}/impact`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['deviation', 'detail', deviationId] }),
  });
}
// C-9 — request an investigation extension (new due date + justification).
export function useDeviationExtensionRequest(
  deviationId: string | undefined,
): UseMutationResult<unknown, Error, { requestedDue: string; justification: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { requestedDue: string; justification: string }) =>
      apiFetch(`/api/v1/deviations/${encodeURIComponent(deviationId!)}/extension`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['deviation', 'detail', deviationId] }),
  });
}
// C-9 — approve / reject a pending extension.
export function useDeviationExtensionDecide(
  deviationId: string | undefined,
): UseMutationResult<unknown, Error, { extensionId: string; decision: 'APPROVED' | 'REJECTED'; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ extensionId, decision, note }: { extensionId: string; decision: 'APPROVED' | 'REJECTED'; note?: string }) =>
      apiFetch(`/api/v1/deviations/${encodeURIComponent(deviationId!)}/extension/${encodeURIComponent(extensionId)}/decide`, { method: 'POST', body: { decision, note } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['deviation', 'detail', deviationId] }),
  });
}
